import { Collapse, Tooltip } from "@blueprintjs/core";
import React from "react";

import { CourseWrapper } from "common/entities/courseWrapper/CourseWrapper";
import { completionLabel } from "common/entities/courseWrapper/labels";
import { ID } from "common/entities/IEntity";
import ProgramWrapper from "common/entities/programWrapper/ProgramWrapper";
import { FlexJustify, FlexLeft } from "common/fragments/Flex";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { HTML, TextStoreRenderer } from "common/fragments/TextContent";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { BoxVariant, BoxView } from "common/views/box/Box.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { CardField, CardVariant, CardView } from "common/views/card/Card.view";
import HeadingView, { HeadingVariant } from "common/views/heading/Heading.view";
import IconView from "common/views/icon/Icon.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import ProgressBarView from "common/views/progressBar/ProgressBar.view";
import ProgressCircleView from "common/views/progressCircle/ProgressCircle.view";
import { RowsView } from "common/views/rows/Rows.view";
import QueryComponent from "core/api/QueryComponent";
import { Query } from "core/api/typedQuery";
import { IPanelProps, Panel } from "core/decorators/Panel";
import Program from "./entities/Program";
import ProgramTrack from "./entities/ProgramTrack";
import ProgramTrackUser from "./entities/ProgramTrackUser";
import { SubscriptionTag } from "./entities/SubscriptionTag";
import labelProvider from "./Program.labels";
import ProgramConfig from "./ProgramConfig";

const { Labels } = labelProvider;

/**
 * @description renders details of a selected program or a program identified by page param with an overindulgent amount of status indicators
 * @author chills
 */

interface IProgramParams {
	id: ID;
}

interface IProgramTrackUserParams {
	programId: ID;
}

const OneProgram = Query.queryOne<Program, IProgramParams>(Program);
const ManyProgramTrackUsers = Query.queryMany<ProgramTrackUser, IProgramTrackUserParams>(ProgramTrackUser);

export const PARAM_PROGRAM_ID = "programId";

interface IProgramPanelState {
	open: { [key: string]: boolean; };
}

@Panel({
	id: "program",
	configClass: ProgramConfig,
	pageParams: true,
	labelProvider
})
export default class ProgramPanel extends React.Component<IPanelProps<ProgramConfig>, IProgramPanelState> {

	public state = { open: {} };

	public render() {
		const { config, pageParams } = this.props;

		const id: number = config.programId || pageParams[PARAM_PROGRAM_ID];

		if (!id) {
			return (
				<NonIdealState
					title={<Labels.ProgramNotFoundTitle />}
					description={<Labels.ProgramNotFoundDesc />}
				/>
			);
		}

		return (
			<div>
				<QueryComponent entity={Program} type="one" args={{id}}>
					{({data, error, loading}) => {

						if (loading) {
							return <Loading />;
						}

						if (error) {
							return null;
						}

						const pw = new ProgramWrapper(data);

						const pretest = pw.getPreTest();
						const posttest = pw.getPostTest();

						return [

							// program details

							<BlockView key="program" variant={BlockVariant.JUMBOTRON} viewKey="program">

							</BlockView>,

							// pretest, if configured for this program

							pretest && (
								<BlockView key="pretest" viewKey="pretest">

								</BlockView>
							),

							// tracks

							config.wrapTracks ? (
								<BlockView key="tracks" viewKey="tracks">
									tracks
								</BlockView>
							) : (
								<React.Fragment key="tracks">
									tracks
								</React.Fragment>
							),

							// posttest, if configured

							posttest && (
								<BlockView viewKey="posttest">
									posttest
								</BlockView>
							)
						];
					}}
				</QueryComponent>
			</div>
		);
	}

	/**
	 * match pretest sub tags by ID and determine which topics are weaknesses
	 * there's probably a better way to do this...
	 */
	private renderTrackProducts = (track: ProgramTrack, weaknesses: SubscriptionTag[]) => {
		if (!track.products || track.products.length < 0) {
			return (
				<NonIdealState
					title={<Labels.ProductsEmptyTitle />}
					description={<Labels.ProductsEmptyDesc />}
				/>
			);
		}

		return (
			<RowsView viewKey="track-product-rows">
				{
					track.products.map((trackProduct) => {
						const { entry } = trackProduct;
						if (!entry) {
							return null;
						}

						const w = CourseWrapper.forEntry(entry);

						const itemTagIds = w.getTags().map((t) => t.id);

						const lst = [];

						for (const st of weaknesses) {
							if (itemTagIds.indexOf(st.tag.id) > -1) {
								lst.push(st.tag);
							}
						}

						return (
							<CardView key={entry.id} viewKey="course">
								<BlockView viewKey="course-content" variant={BlockVariant.CARD_CONTENT}>
									<div style={{display: 'flex', justifyContent: 'space-between'}}>
										<div style={{flexGrow: 1}}>
											<CardField>
												<div style={{display: 'flex', justifyContent: 'space-between'}}>
													<div>
														<HeadingView style={{maxWidth: 600}} variant={HeadingVariant.H5} viewKey="course-title" >{w.getTitle()}</HeadingView>
														{
															w.getSubtitle() && (
																<HeadingView viewKey="course-subtitle">
																	{w.getSubtitle()}
																</HeadingView>
															)
														}
														<div>
															{null}
														</div>
													</div>
													{
														lst.length > 0 && (
															<div>
																<Tooltip content={<Labels.Weaknesses />}>
																	<IconView icon="ban-circle" viewKey="weakness" />
																</Tooltip>
															</div>
														)
													}
												</div>
											</CardField>
											<CardField>
												<ProgressBarView value={w.getProgressPercent()} viewKey="course-progress" />
											</CardField>
										</div>
										<div>
											{
												w.isLaunchable() && (
													<ButtonView variant={ButtonVariant.PRIMARY} onClick={() => w.launchCourse()} viewKey="course-launch">
														<Labels.Launch />
													</ButtonView>
												)
											}
										</div>
									</div>
								</BlockView>
							</CardView>
						);
					})
				}
			</RowsView>
		);
	}

	private expandTrack = (id: any) => this.setState({...this.state, open: { ...this.state.open, [id]: !!!this.state.open[id] }});
}


	/*
		return (
			<div>
				<OneProgram params={{ id }}>
					{
						({ data, loading }) => {
							if (loading) {
								return <Loading />;
							}

							if (!data) {
								return "Learning Path Not Found";
							}

							const pw = new ProgramWrapper(data);

							// top-level progress
							const totals = pw.getAggregateProgress();
							const progressValue: number = pw.getAggregateProgressPercent();

							// pretest and post test
							const pretest = pw.getPreTestWrapper();
							const posttest = pw.getPostTestWrapper();

							// subscription tags from pretest for comparing with product tags
							const weaknesses = pretest ? pretest.getWeaknesses() : [];

							return [
								<CardView key="info" variant={CardVariant.MINIMAL} viewKey="info">
									<HeadingView children={pw.getName()} variant={HeadingVariant.H1} viewKey="name" />
									{
										(showProgress) && (
											<ProgressBarView percent={progressValue} viewKey="program-progress" />
										)
									}
									<TextStoreRenderer {...data.description} />
								</CardView>,
								<BoxView key="sections" variant={BoxVariant.MINIMAL} title={<Labels.TracksTitle />} viewKey="sections">
									{
										pretest && (
											<CardView variant={CardVariant.SURFACE} viewKey="section">
												<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="section-content">
													<FlexJustify>
														<div>
															<FlexLeft>
																{
																	pretest.getCompletion() && (
																		<div style={{marginRight: "15px"}}>
																			<Tooltip content={React.createElement(completionLabel(pretest.getCompletion()))}>
																				<ProgressCircleView value={pretest.getProgressPercent()} viewKey="program-progress-circle" />
																			</Tooltip>
																		</div>
																	)
																}
																<div>
																	<HeadingView variant={HeadingVariant.H3} viewKey="section-title">{pretest.getTitle()}</HeadingView>
																	{
																		pretest.getSubtitle() && (
																			<HeadingView variant={HeadingVariant.H5} viewKey="section-subtitle">
																				{pretest.getSubtitle()}
																			</HeadingView>
																		)
																	}
																	{
																		(pretest.isCompleted() && pretest.getCompletionDate()) && (
																			<span>
																				<Labels.CompletedOn args={{date: new Date(pretest.getCompletionDate())}} />
																			</span>
																		)
																	}
																</div>
															</FlexLeft>
														</div>
														{
															pretest.isCompleted() && (
																<FlexLeft style={{alignItems: "center"}}>
																	<ProgressCircleView value={pretest.getScorePercent()} viewKey="program-score-circle" />
																	<div>
																		<HeadingView viewKey="program-stat">
																			<Labels.Score args={{score: `${Math.round(pretest.getScorePercent() * 100)}%`}} />
																		</HeadingView>
																	</div>
																</FlexLeft>
															)
														}
														<div>
															<ButtonView variant={ButtonVariant.PRIMARY} onClick={() => pretest.launchCourse()} viewKey="test-launch">
																<Labels.Launch />
															</ButtonView>
														</div>
													</FlexJustify>
												</BlockView>
											</CardView>
										)
									}
									<CardView variant={CardVariant.SURFACE} viewKey="section">
										<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="section-content">
											<FlexLeft>
												<div style={{marginRight: "15px"}}>
													<Tooltip content={`${totals.completed} / ${totals.total}`}>
														<ProgressCircleView value={progressValue} viewKey="program-progress-circle" />
													</Tooltip>
												</div>
												<div>
													<HeadingView variant={HeadingVariant.H3} viewKey="section-title">
														<Labels.TracksSectionTitle />
													</HeadingView>
													<HeadingView variant={HeadingVariant.H5} viewKey="section-subtitle">
														<Labels.TracksSectionSubTitle />
													</HeadingView>
													<HeadingView variant={HeadingVariant.H6} viewKey="section-desc">
														<Labels.TracksSectionStats args={totals} />
													</HeadingView>
													{
														weaknesses && weaknesses.length > 0 && (
															<HeadingView viewKey="program-weaknesses-legend">
																<FaIconView icon={FaIconNames.EXCLAMATION_CIRCLE} viewKey="weakness">
																	<Labels.Weaknesses />
																</FaIconView>
															</HeadingView>
														)
													}
												</div>
											</FlexLeft>
										</BlockView>
										<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="section-content">
											<ManyProgramTrackUsers params={{ programId: id }}>
												{
													(trackArgs) => {
														if (trackArgs.loading) {
															return <Loading />;
														}

														if (isEmpty(trackArgs.data)) {
															return (
																<NonIdealState
																	title={<Labels.TracksEmptyTitle />}
																	description={<Labels.TracksEmptyDesc />}
																/>
															);
														}

														const trackList = trackArgs.data;

														return (
															<div>
																<RowsView viewKey="track-rows">
																	{
																		trackList.map((trackUser) => {
																			const { programTrack } = trackUser;

																			if (!programTrack) {
																				return null;
																			}

																			const trackProgressValue = (programTrack.progress && programTrack.progress.total > 0)
																				? (programTrack.progress.completed / programTrack.progress.total) : 0;

																			const primary = (
																				<div style={{flexGrow: 1}}>
																					<CardField>
																						<HeadingView key="name" viewKey="track-title" children={programTrack.name} />
																					</CardField>
																					{programTrack.description && (
																						<CardField>
																							<HTML key="description" content={programTrack.description} />
																						</CardField>
																					)}
																					{programTrack.progress && showTrackProgress && (
																						<CardField>
																							<ProgressBarView
																								key="progress"
																								percent={trackProgressValue}
																								viewKey="track-progress"
																							/>
																						</CardField>
																					)}
																				</div>
																			);

																			const isOpen = this.state.open[trackUser.id] === true;

																			return (
																				<div key={trackUser.id}>
																					<CardView variant={CardVariant.ALT} interactive={true} onClick={() => this.expandTrack(trackUser.id)} viewKey="track-card">
																						<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="track-card-content">
																							<FlexJustify>
																								{primary}
																								<FaIconView
																									key="icon"
																									viewKey={"track-expand-icon" + (isOpen ? "-open" : "-closed")}
																									icon={isOpen ? FaIconNames.ANGLE_DOWN : FaIconNames.ANGLE_UP}
																								/>
																							</FlexJustify>
																						</BlockView>
																					</CardView>
																					<Collapse isOpen={isOpen}>
																						<BlockView viewKey="track.collapse">
																							{
																								enableTrackDetailsLink && (
																									<div>
																										<PageLinkView key={trackUser.id} params={{trackName: programTrack.name}} viewKey="track-details-link">
																											<ButtonView viewKey="track-details">
																												<Labels.View />
																											</ButtonView>
																										</PageLinkView>
																									</div>
																								)
																							}
																							{this.renderTrackProducts(programTrack, weaknesses)}
																						</BlockView>
																					</Collapse>
																				</div>
																			);
																		})
																	}
																</RowsView>
															</div>
														);
													}
												}
											</ManyProgramTrackUsers>
										</BlockView>
									</CardView>
									{
										posttest && (
											<CardView variant={CardVariant.SURFACE} viewKey="section">
												<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="section-content">
													<FlexJustify>
														<div>
															<FlexLeft>
																{
																	posttest.getCompletion() && (
																		<div style={{marginRight: "15px"}}>
																			<Tooltip content={React.createElement(completionLabel(posttest.getCompletion()))}>
																				<ProgressCircleView value={posttest.getProgressPercent()} viewKey="program-progress-circle" />
																			</Tooltip>
																		</div>
																	)
																}
																<div>
																<HeadingView variant={HeadingVariant.H3} viewKey="section-title">{posttest.getTitle()}</HeadingView>
																	{
																		posttest.getSubtitle() && (
																			<HeadingView variant={HeadingVariant.H5} viewKey="section-subtitle">{posttest.getSubtitle()}</HeadingView>
																		)
																	}
																	{
																		(posttest.isCompleted() && posttest.getCompletionDate()) && (
																			<span>
																				<Labels.CompletedOn args={{date: new Date(posttest.getCompletionDate())}} />
																			</span>
																		)
																	}
																</div>
															</FlexLeft>
														</div>
														{
															posttest.isCompleted() && (
																<FlexLeft style={{alignItems: "center"}}>
																	<ProgressCircleView value={posttest.getScorePercent()} viewKey="program-progress-circle" />
																	<div>
																		<HeadingView viewKey="program-stat">{`${posttest.getScorePercent() * 100}%`}</HeadingView>
																	</div>
																</FlexLeft>
															)
														}
														<div>
															<ButtonView onClick={() => posttest.launchCourse()} variant={ButtonVariant.PRIMARY} viewKey="test-launch">
																<Labels.Launch />
															</ButtonView>
														</div>
													</FlexJustify>
												</BlockView>
											</CardView>
										)
									}
								</BoxView>
							];
						}
					}
				</OneProgram>
			</div>
		);
		*/



// WEBPACK FOOTER //
// ./src/panels/program/Program.panel.tsx